import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type {
  SchedulesParams,
  SchedulesResponse,
} from 'src/apis/transport-auction/types';
import type { OptionalTransportAuctionServiceRootRequest } from 'src/apis/transport-auction/use-transport-auction-service-root';
import { useAxios } from 'src/hooks/use-axios';
import { isConstraintViolation } from 'src/utils/is-constraint-violation';

type HookOptions = {
  root: OptionalTransportAuctionServiceRootRequest;
  params: SchedulesParams;
};

type ExecuteOptions = {
  url: string;
  params: SchedulesParams;
};

export function useSchedules({ root, params }: HookOptions) {
  const schedules = useAxios(
    (axios, baseConfig, { url, params }: ExecuteOptions) =>
      axios.request<SchedulesResponse>({
        ...baseConfig,
        url: urlTemplate.parse(url).expand({}),
        params,
      }),
    {
      neededOnPageLoad: true,
      onError(error) {
        if (isConstraintViolation(error)) {
          return error;
        }
        throw error;
      },
    }
  );

  const schedulesLink = root?.response?.data._links.schedules;

  useEffect(() => {
    if (!schedulesLink) return;

    schedules.execute({
      url: schedulesLink.href,
      params,
    });
    // oxlint-disable-next-line exhaustive-deps
  }, [schedulesLink, params]);

  if (!schedulesLink) return;

  return schedules;
}

export type SchedulesRequest = ReturnType<typeof useSchedules>;
